import i18next from 'i18next'
import { List } from 'immutable'
import React from 'react'
import { InView } from 'react-intersection-observer'
import Slider from 'react-slick'
import { blockInviewHandler } from '../../../../../utils/inview'
import { ComponentDidMount } from '../../../../../utils/lifecycleMethods'
import { PictureLayout } from '../../../image/imageLayout'
import { SlideImageData } from '../loggedOutHomeState'
import { homeInViewThreshold } from '../loggedOutHomeWidget'

const customPaging = (i: number) => (
  <button>
    {i18next.t('home:quoteDot', { count: i + 1 })}
    <svg width="13" height="13" aria-hidden="true">
      <use xlinkHref="#icon--hexagon-full" />
    </svg>
  </button>
)

const getRandomNItens = <T,>(arr: T[], nItems: number): T[] => {
  let result = List(arr)
  for (let i = 0; i < result.count(); i++) {
    let j = Math.round(Math.random() * (result.count() - 1))
    let fst = result.get(i)
    let snd = result.get(j)
    if (fst != undefined && snd != undefined) {
      result = result.set(j, fst)
      result = result.set(i, snd)
    }
  }
  return result.take(nItems).toArray()
}

export const HomeQuoteCarouselBlockLayout = ({
  slides,
  setSlides,
}: {
  slides: SlideImageData[]
  setSlides: (data: SlideImageData[]) => void
}) => (
  <>
    <ComponentDidMount
      onMount={() => setSlides(getRandomNItens(chefImages, 5))}
    />
    <InView
      as="section"
      threshold={homeInViewThreshold}
      triggerOnce={true}
      onChange={(isInView, entry) => blockInviewHandler(isInView, entry.target)}
      className="block block--home-quote-carousel"
    >
      <div className="block__inner">
        <div className="block__content">
          <Slider
            dots={true}
            infinite={false}
            slidesToShow={4}
            slidesToScroll={1}
            arrows={false}
            focusOnSelect={true}
            speed={600}
            customPaging={customPaging}
            responsive={[
              {
                breakpoint: 1280, // read as 'max-width: 1279px'
                settings: {
                  slidesToShow: 3,
                  slidesToScroll: 1,
                },
              },
              {
                breakpoint: 1024, // read as 'max-width: 1023px'
                settings: {
                  slidesToShow: 2,
                  slidesToScroll: 1,
                },
              },
              {
                breakpoint: 768, // read as 'max-width: 767px'
                settings: {
                  slidesToShow: 1,
                  slidesToScroll: 1,
                },
              },
            ]}
          >
            {slides.map((slide) => (
              <Slide
                key={slide.Id}
                image={slide}
                quote={i18next.t(`home:quote${slide.Id}Text`)}
                name={i18next.t(`home:quote${slide.Id}AuthorName`)}
                function={i18next.t(
                  `home:quote${slide.Id}AuthorFunction`
                )}
              />
            ))}
          </Slider>
        </div>
      </div>
    </InView>
  </>
)

const Slide = (props: {
  image: SlideImageData
  quote: string
  name: string
  function: string
}) => (
  <div className="slide__inner">
    <blockquote className="slide__blockquote">
      <p
        className="slide__quote quote--m"
        dangerouslySetInnerHTML={{ __html: props.quote }}
      />
      <footer className="slide__footer">
        <figure className="slide__figure">
          <figcaption className="slide__figcaption bg">
            <p>{props.name}</p>
            <p>{props.function}</p>
          </figcaption>
          <PictureLayout.Img
            image={props.image.imageData}
            srcSet={props.image.SrcSet}
            sizes="(min-width: 80rem) calc(100vw - (3 * 11.25rem)), (min-width: 64rem) calc(100vw - (2 * 11.25rem)), (min-width: 48rem) calc(100vw - 11.25rem), 100vw"
            width="960"
            height="800"
            extraClasses="slide__image"
            lazyload
          />
          <div className="slide__fade" aria-hidden="true" />
        </figure>
      </footer>
    </blockquote>
  </div>
)

// The chef's name (including capitals) should match the name in the image file
const chefNames: string[] = [
  'Ben Murphy',
  'Merijn van Berlo',
  'Jp McMahon',
  'Benoit de Witte',
  'Mark Best',
  'Martin Benn',
  'Jonnie Boer',
  'Sidney Schutte',
  'Emiel Kwekkeboom',
  'Olly Ceulenaere',
  'Cas Pikaar',
  'Angelique Schmeinck',
  'Marleen Brouwer',
  'Jermain de Rozario',
  'Christian Puglisi',
  'Wouter van Laarhoven',
  'Adam Handling',
  'Bas van Kranen',
  'Gareth Ward',
  'Nathan Davies',
  'Soenil Bahadoer',
  // 'Timon Michiels' // TODO add this one when we receive the correct image
]

const slugifyName: (str: string) => string = (str: string) =>
  str
    .toString()
    .replace(/\s+/g, '-')
    .replace(/[^\w\-]+/g, '')
    .replace(/\-\-+/g, '-')
    .replace(/^-+/, '')
    .replace(/-+$/, '')

export const chefImages: SlideImageData[] = chefNames.map((chef, key) => ({
  imageData: {
    Title: chef,
    Url: `/spa/images/chefs/${key + 1}-${slugifyName(
      chef
    )}-testimonial-medium.jpg`,
  },
  Id: key + 1,
  // Don't use small (480w) image src, otherwise the preview panels are blurry on desktop
  SrcSet: `/spa/images/chefs/${key + 1}-${slugifyName(
    chef
  )}-testimonial-medium.jpg 960w, /spa/images/chefs/${key + 1}-${slugifyName(
    chef
  )}-testimonial-large.jpg 1920w`,
}))
